import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "expertise-selection" }
const _hoisted_2 = { class: "expertise-selection__title" }
const _hoisted_3 = { class: "expertise-selection__title-text" }

import getters from '@/profile-creation-wizard/store/creation/getters';
    import CategorizationManualSelection from '@/common/components/categorization/CategorizationManualSelection.vue';
    import CategorizationPrediction from '@/common/components/categorization/CategorizationPrediction.vue';
    import CategorizationLevelSelection from '@/profile-creation-wizard/components/for-creation/ui/2_categorization/CategorizationLevelSelection.vue';
    import CategorizationSide from '@/profile-creation-wizard/components/for-creation/ui/2_categorization/CategorizationSide.vue';
    import CategorizationDetailsSkeleton from '@/common/components/categorization/CategorizationDetailsSkeleton.vue';
    import {PredictCategoryResource, ProfileFamily} from '@/profile-creation-wizard/common/categories/FamiliesCategories';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import Step from '@/profile-creation-wizard/common/ui/step/Step.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {computed, inject, onMounted, reactive, ref} from 'vue';
    import {useStore} from 'vuex';
    import {useRouter} from 'vue-router';
    import {useTranslation} from '@malt/vue3-use';
    import {ProfileCreationFunnelMode} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';
    import ReroutingToOpenMarketplaceCategory from './ReroutingToOpenMarketplaceCategory.vue';
    import ReroutingToOpenMarketplaceExperienceLevel from './ReroutingToOpenMarketplaceExperienceLevel.vue';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'CategorizationStep',
  setup(__props) {

    const store = useStore();
    const router = useRouter();
    const {msg} = useTranslation();

    const categorizationRepository = inject(DI.categorizationRepository);
    const creationFormValidation = inject(DI.creationFormValidation);
    const trackingRepository = inject(DI.trackingRepository);
    const creationForm = ref(store.getters[getters.creationForm]);

    const state = reactive<{
        families: ProfileFamily[];
        predictions: PredictCategoryResource[];
        predictionsKeeper: PredictCategoryResource[];
        hadPredictions: boolean;
        isLoadingPredictions: boolean;
    }>({
        families: [],
        predictions: [],
        predictionsKeeper: [],
        hadPredictions: false,
        isLoadingPredictions: true,
    });

    const funnelMode = computed(() => router.currentRoute.value.meta.funnelMode);
    const isStrategyFunnelMode = computed(() => funnelMode.value === ProfileCreationFunnelMode.STRATEGY);

    const expertiseTitle = computed(() =>
        msg(`profile.creation.completion.wizard.v2.expertise.${isStrategyFunnelMode.value ? 'strategy.' : ''}title`),
    );
    const hasPredictions = computed(() => state.predictions.length > 0);
    const isStepValid = computed(
        () =>
            creationForm.value.familiesCategoriesEdition.hasCompleteSelection() &&
            creationFormValidation!.isExperienceLevelValid(creationForm.value.experienceLevel) &&
            !showExperienceLevelRerouting.value &&
            !showCategoryRerouting.value,
    );
    const showExperienceLevelRerouting = ref(false);
    const showCategoryRerouting = ref(false);

    onMounted(async () => {
        await getPredictions();
        await loadFamilies();
    });

    async function getPredictions(): Promise<void> {
        if (state.predictionsKeeper.length > 0) {
            state.predictions = [...state.predictionsKeeper];
            return;
        }

        state.isLoadingPredictions = true;
        const [predictions, error] = await categorizationRepository!.predict({
            headline: creationForm.value.headline as string,
            vertical: isStrategyFunnelMode.value ? ProfileCreationFunnelMode.STRATEGY : ProfileCreationFunnelMode.FREELANCER,
        });

        if (predictions && !error) {
            state.predictions = predictions;
            state.hadPredictions = state.predictions.length > 0;
        }

        state.isLoadingPredictions = false;
    }

    async function loadFamilies(): Promise<void> {
        let [families, error] = await categorizationRepository!.getAll();

        const host = window.location.host;
        if (error && (host.startsWith('dev') || host.startsWith('localhost'))) {
            console.error('Could not load families, falling back to mock data', error);
            error = null;
            families = [
                {
                    familyId: 'tech',
                    label: 'Tech',
                    pictoName: 'code',
                    categories: [
                        {categoryId: 'backend_developer', label: 'Développeur Web Back-end', verticals: ['FREELANCER']},
                        {categoryId: 'frontend_developer', label: 'Développeur Web Front-end', verticals: ['FREELANCER']},
                    ],
                },
                {
                    familyId: 'data',
                    label: 'Expert Data',
                    pictoName: 'dataviz',
                    categories: [
                        {categoryId: 'data_scientist', label: 'Data Scientist', verticals: ['FREELANCER']},
                        {categoryId: 'data_engineer', label: 'Data Engineer', verticals: ['FREELANCER']},
                    ],
                },
            ];
        }

        if (families && !error) {
            state.families = families;
            store.commit(mutations.loadFamiliesDefinition, families);
        }
    }

    async function switchToManualSelection(): Promise<void> {
        if (state.families.length === 0) {
            await loadFamilies();
        }

        state.predictionsKeeper = [...state.predictions];
        state.predictions = [];
    }

    function manualSelection(): void {
        store.commit(mutations.switchToCategoryManual);
    }

    function predictionSelection(): void {
        store.commit(mutations.switchToCategoryPrediction);
    }

    function manualSelectFamily(familyId: string): void {
        store.commit(mutations.selectManualFamily, familyId);
    }

    function manualSelectCategory(categories: string[]): void {
        store.commit(mutations.selectManualCategories, categories);
    }

    function manualSelectOtherCategory(otherCategory: string): void {
        store.commit(mutations.selectManualSuggestedCategory, otherCategory);
    }

    function manualUnselectCategories(): void {
        store.commit(mutations.unselectManualCategories);
    }

    function selectFamilyAndCategory(familyAndCategory: any): void {
        const action = familyAndCategory.action;
        const selection = {
            family: familyAndCategory.familyId,
            category: familyAndCategory.categoryId,
        };
        if (action === 'unselect') {
            store.commit(mutations.unselectPredictionFamilyAndCategory, selection);
        } else {
            store.commit(mutations.selectPredictionFamilyAndCategory, selection);
        }
    }

    function updateExperienceLevelValidation(isVisible: boolean): void {
        showExperienceLevelRerouting.value = isVisible;
    }

    function updateCategoryValidation(isVisible: boolean): void {
        showCategoryRerouting.value = isVisible;
    }

    function goNext(): void {
        if (!isStepValid.value) {
            return;
        }
        const familiesAndCategories = creationForm.value.familiesCategoriesEdition.toRequest();
        trackingRepository!.track(`categorization-chosen-${store.state.funnelMode}`, {
            families: familiesAndCategories.families,
            categories: familiesAndCategories.categories,
            suggestionCategory: familiesAndCategories.categorySuggestion,
            fromCategoryPredictor: state.predictions.length > 0,
        });

        trackingRepository!.track(`expertise-level-chosen-${store.state.funnelMode}`, {
            expertiseLevel: creationForm.value.experienceLevel,
        });

        router.push({name: router.currentRoute.value.meta.nextStep});
    }

    function goToPrevious(): void {
        router.push({name: router.currentRoute.value.meta.previousStep});
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Step, {
    "is-step-valid": isStepValid.value,
    onGoToNext: goNext
  }, {
    "step-content": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(expertiseTitle.value), 1),
          _createElementVNode("div", {
            class: _normalizeClass(["expertise-selection__title-tag responsive-content", {'expertise-selection__title-tag--strategy': isStrategyFunnelMode.value}]),
            onClick: goToPrevious
          }, _toDisplayString(creationForm.value.headline), 3)
        ]),
        (state.isLoadingPredictions)
          ? (_openBlock(), _createBlock(CategorizationDetailsSkeleton, { key: 0 }))
          : _createCommentVNode("", true),
        (hasPredictions.value && !state.isLoadingPredictions)
          ? (_openBlock(), _createBlock(CategorizationPrediction, {
              key: 1,
              predictions: state.predictions,
              categories: creationForm.value.familiesCategoriesEdition.allSelectedCategories(),
              onInputFamilyCategory: selectFamilyAndCategory,
              onManualSelection: switchToManualSelection,
              onPredictionSelection: predictionSelection
            }, null, 8, ["predictions", "categories"]))
          : _createCommentVNode("", true),
        (!hasPredictions.value && !state.isLoadingPredictions)
          ? (_openBlock(), _createBlock(CategorizationManualSelection, {
              key: 2,
              families: state.families,
              "preferred-family": creationForm.value.familiesCategoriesEdition.preferredFamily(),
              categories: creationForm.value.familiesCategoriesEdition.allSelectedCategories(),
              hadPredictions: state.hadPredictions,
              suggestedCategory: creationForm.value.familiesCategoriesEdition.suggestedCategory(),
              "closest-parent-selector": "div.profile-creation-wizard",
              "bottom-sheet-selector": "#expertise-details-bottom-sheet",
              onInputFamily: manualSelectFamily,
              onInputCategory: manualSelectCategory,
              onOtherCategoryChange: manualSelectOtherCategory,
              onUnselectCategories: manualUnselectCategories,
              onGoBack: getPredictions,
              onManualSelection: manualSelection
            }, null, 8, ["families", "preferred-family", "categories", "hadPredictions", "suggestedCategory"]))
          : _createCommentVNode("", true)
      ]),
      (!state.isLoadingPredictions && isStrategyFunnelMode.value)
        ? (_openBlock(), _createBlock(ReroutingToOpenMarketplaceCategory, {
            key: 0,
            onCategoryRerouting: updateCategoryValidation
          }))
        : _createCommentVNode("", true),
      (!state.isLoadingPredictions)
        ? (_openBlock(), _createBlock(CategorizationLevelSelection, {
            key: 1,
            class: "experience-level-selection",
            "bottom-sheet-selector-prop": "#expertise-details-experience-bottom-sheet"
          }))
        : _createCommentVNode("", true),
      (!state.isLoadingPredictions && isStrategyFunnelMode.value)
        ? (_openBlock(), _createBlock(ReroutingToOpenMarketplaceExperienceLevel, {
            key: 2,
            onExperienceLevelRerouting: updateExperienceLevelValidation
          }))
        : _createCommentVNode("", true)
    ]),
    "step-side": _withCtx(() => [
      _createVNode(CategorizationSide)
    ]),
    _: 1
  }, 8, ["is-step-valid"]))
}
}

})